import { useState, type ReactNode } from 'react';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  placement?: TooltipPlacement;
  disabled?: boolean;
  className?: string;
}

export const Tooltip = ({
  children,
  content,
  placement = 'top',
  disabled = false,
  className = ''
}: TooltipProps) => {
  
  const [isVisible, setIsVisible] = useState(false);

  if (disabled || !content) {
    return <div className="inline-block">{children}</div>;
  }

  const showTooltip = () => setIsVisible(true);
  const hideTooltip = () => setIsVisible(false);

  const getTooltipClassName = () => {
    const baseClasses = 'absolute z-50 px-2 py-1 text-sm text-white bg-gray-900 rounded shadow-lg pointer-events-none transition-opacity duration-200 whitespace-nowrap';
    
    const placementClasses = {
      top: 'bottom-full mb-2 left-1/2 transform -translate-x-1/2',
      bottom: 'top-full mt-2 left-1/2 transform -translate-x-1/2',
      left: 'right-full mr-2 top-1/2 transform -translate-y-1/2',
      right: 'left-full ml-2 top-1/2 transform -translate-y-1/2'
    };

    const visibilityClasses = isVisible ? 'opacity-100' : 'opacity-0';

    return `${baseClasses} ${placementClasses[placement]} ${visibilityClasses} ${className}`;
  };

  const getArrowClassName = () => {
    const arrowClasses = {
      top: 'absolute top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900',
      bottom: 'absolute bottom-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-900',
      left: 'absolute left-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-l-4 border-t-transparent border-b-transparent border-l-gray-900',
      right: 'absolute right-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent border-r-gray-900'
    };

    return arrowClasses[placement];
  };

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      <div
        className={getTooltipClassName()}
        role="tooltip"
      >
        {content}
        <div className={getArrowClassName()} />
      </div>
    </div>
  );
};

// Componente wrapper para casos simples
export const TooltipWrapper = ({ 
  tooltip, 
  children,
  ...props 
}: Omit<TooltipProps, 'content'> & { 
  tooltip: string;
  children: ReactNode;
}) => (
  <Tooltip content={tooltip} {...props}>
    <span className="inline-block">
      {children}
    </span>
  </Tooltip>
);